import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Modal,
  TextField,
  Grid,
  FormControl,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Chip,
} from '@material-ui/core';
import _ from 'lodash';
import axios from 'axios';
import CustomSnackbar from '../../user/shared/CustomSnackbar';
import { Cancel } from '@material-ui/icons';
import RegenerateBio from "../shared/Buttons/RegenerateBio";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    maxHeight: '100%',
    overfnameY: 'auto',
    width: '50%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {
    justifyContent: 'flex-end'
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    margin: '20px 0',
    fontSize: '20px',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  chip: {
    margin: '2px',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  logoActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  logo: {
    width: '100px',
    height: '100px',
  },
}))

const FundedCompanyForm = ({ adminUserSession, fundedCompanyId, onClose, open, afterCreateFundedCompany, onDataUpdate, itemIndex, ...props }) => {
  const classes = useStyles();

  const [fundedCompany, setFundedCompany] = useState({
    name: '',
    crd_id: '',
    industry_ids: [],
    logoPreview: '',
    logo: '',
    item_to_merge: null
  })

  const [errorMessage, setErrorMessage] = useState({});
  const [industries, setIndustries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [fundedData, setFundedData] = useState({open: false, data: [], item_to_merge: {}});

  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [showSnackBar, setShowSnackBar] = useState(false);

  useEffect(() => {
    axios.get(
      `/v1/admin/industries/fintrx_data`,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {
        industries: industriesData,
        status: responseStatus
      } = response.data;

      setIndustries(industriesData);
    })

    axios.get(
      `/v1/admin/regions/fintrx_data`,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {
        regions: regionsData,
        status: responseStatus
      } = response.data;

      setRegions(regionsData);
    })
  }, [])

  useEffect(() => {
    if (fundedCompanyId !== undefined && !_.isEmpty(adminUserSession)) {
      axios.get(
        `/v1/admin/funded_companies/${fundedCompanyId}/edit`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Admin-User-Email': adminUserSession.email,
            'X-Admin-User-Token': adminUserSession.token
          }
        }
      ).then((response) => {
        const {
          funded_company: fundedCompanyData,
          status: responseStatus
        } = response.data;

        const {
          ...itemData
        } = fundedCompanyData

        setFundedCompany({
          ...itemData,
          logoPreview: itemData.logo_url || '',
        });

        handleFetchRegionCountries(fundedCompanyData.region_id);
        handleFetchFundedData({name: fundedCompanyData.name}, true)
      }).catch(function (thrown) {
        console.log('error', thrown.message)
      });
    }
  }, [fundedCompanyId])

  const handleFetchRegionCountries = (regionId) => {
    axios.get(
      `/v1/admin/regions/${regionId}/countries`,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {
        countries: countriesData,
        status: responseStatus
      } = response.data;

      setCountries(countriesData);
    })
  }

  const handleFetchFundedData = (params = {}, initializing = false) => {
    if (fundedCompanyId)
      params = {...params, not_id: fundedCompanyId}

    axios.get(
      '/v1/admin/funded_companies/all_funded_data',
      {
        params: params,
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {funded_data, status: responseStatus} = response.data;

      setFundedData({
        ...fundedData,
        open: initializing ? false : true,
        data: funded_data,
      })

      if (initializing && funded_data.length === 1) {
        handleOnChangeAutocomplete(funded_data[0])
      }
    });
  }

  const handleOpenFundedData = (open) => {
    if (open === true) {
      setFundedData({...fundedData, open: true})
    } else {
      setFundedData({...fundedData, open: false, data: []})
    }
  }

  const handleInputChangeAutocomplete = (event) => {
    event.persist();
    debounceFilterInput(event);
  }

  const debounceFilterInput = _.debounce((event) => {
    handleFetchFundedData({name: event.target.value})
  }, 500)

  const handleOnChangeAutocomplete = (selectedValue) => {
    setFundedData({...fundedData, item_to_merge: selectedValue})
    setFundedCompany(prevState => ({
      ...prevState,
      item_to_merge: selectedValue,
    }));
  }

  const handleCreateFundedCompany = (fundedCompany) => {
    let data = new FormData();
    data.append('funded_company[name]', fundedCompany.name);
    data.append('funded_company[crd_id]', fundedCompany.crd_id || '');
    data.append('funded_company[url]', fundedCompany.url || '');
    data.append('funded_company[address]', fundedCompany.address || '');
    data.append('funded_company[state]', fundedCompany.state || '');
    data.append('funded_company[city]', fundedCompany.city || '');
    data.append('funded_company[region_id]', fundedCompany.region_id);
    data.append('funded_company[country_id]', fundedCompany.country_id);

    for (var i = 0; i < fundedCompany.industry_ids.length; i++) {
      data.append('funded_company[industry_ids][]', fundedCompany.industry_ids[i]);
    }

    if (fundedCompany.logoPreview !== fundedCompany.logo_url) {
      data.append('funded_company[logo]', fundedCompany.logo);
    }

    axios.post(
      `/v1/admin/funded_companies`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        },
      }
    ).then((response) => {
      const {
        data: {
          status,
          message,
          funded_company,
        }
      } = response;

      if (status === 'success') {
        afterCreateFundedCompany(funded_company);
        onClose();
      } else {
        setSnackBarMessage(message);
        setShowSnackBar(true);
        console.log('message', message)
      }
    })
  }

  const handleUpdateFundedCompany = (fundedCompany) => {
    let data = new FormData();
    data.append('funded_company[name]', fundedCompany.name);
    data.append('funded_company[crd_id]', fundedCompany.crd_id || '');
    data.append('funded_company[url]', fundedCompany.url || '');
    data.append('funded_company[address]', fundedCompany.address || '');
    data.append('funded_company[state]', fundedCompany.state || '');
    data.append('funded_company[city]', fundedCompany.city || '');
    data.append('funded_company[region_id]', fundedCompany.region_id);
    data.append('funded_company[country_id]', fundedCompany.country_id);

    for (var i = 0; i < fundedCompany.industry_ids.length; i++) {
      data.append('funded_company[industry_ids][]', fundedCompany.industry_ids[i]);
    }

    if (fundedCompany.logoPreview != fundedCompany.logo_url) {
      data.append('funded_company[logo]', fundedCompany.logo);
    }

    axios.put(
      `/v1/admin/funded_companies/${fundedCompanyId}`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {
        status: statusUpdate,
        message,
        funded_company,
      } = response.data;

      if (statusUpdate === 'success') {
        onDataUpdate(funded_company, itemIndex);
      } else {
        setErrorMessage(message);
        setSnackBarMessage(message);
        setShowSnackBar(true);
        console.log('message', message)
      }
    });
  }

  const handleOnChange = (event) => {
    event.persist();

    setFundedCompany(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  }

  const handleOnChangeRegion = (event) => {
    event.persist();

    handleFetchRegionCountries(event.target.value);

    setFundedCompany(prevState => ({
      ...prevState,
      region_id: event.target.value,
      country_id: undefined,
    }));
  }

  const handleSubmit = (event) => {
    event.persist();
    event.preventDefault();

    const blankValues = Object.keys(_.pickBy(fundedCompany, (item) => ([null, undefined, ''].includes(item))));

    if (
      !_.isEmpty(_.intersection(blankValues, ['name', 'industry_ids']))
    ) {
      let message = {};

      blankValues.forEach((value) => {
        message[value] = "Can't be blank!"
      })

      setErrorMessage(message);
    } else if (fundedCompanyId !== undefined) {
      handleUpdateFundedCompany(fundedCompany);
    } else {
      handleCreateFundedCompany(fundedCompany);
    }
  }

  const onRegenerateAiBio = () => {

    axios.put(
        `/v1/admin/funded_companies/${fundedCompanyId}/regenerate_ai_bio`,
        {},
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Admin-User-Email': adminUserSession.email,
            'X-Admin-User-Token': adminUserSession.token
          }
        }
    ).then((response) => {
      const {
        status: statusUpdate,
        message,
      } = response.data;

      if (statusUpdate === 'success') {
        alert(message)
      } else {
        setErrorMessage(message);
        setSnackBarMessage(message);
        setShowSnackBar(true);
        console.log('message', message)
      }
    });

  }

  const handleMerge = (event) => {
    event.persist();
    event.preventDefault();

    let data = new FormData();
    data.append("id", fundedCompanyId)
    data.append("merge_from_id", fundedCompany.item_to_merge?.id)

    axios.post(
      `/v1/admin/funded_companies/merge_with/`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': adminUserSession.email,
          'X-Admin-User-Token': adminUserSession.token
        }
      }
    ).then((response) => {
      const {
        status: status,
        message,
        funded_company,
      } = response.data;

      if (status === 'success') {
        setSnackBarMessage('Items has been merged')
        setShowSnackBar(true);
        setFundedCompany(funded_company)
        onDataUpdate(funded_company, itemIndex, true);
      } else {
        setErrorMessage(message);
        setSnackBarMessage(message);
        setShowSnackBar(true);
        console.log('message', message)
      }
    });
  }

  const handleCloseSnackBar = () => {
    setShowSnackBar(false);
    setSnackBarMessage('');
  }

  const handleRemoveFromMultiple = (event, value, field) => {
    event.persist();

    let newData = [...fundedCompany[field]];

    newData = newData.filter((datumId) => datumId !== value);

    setFundedCompany(prevState => ({
      ...prevState,
      [field]: newData,
    }));
  }

  const handleChangeFileInput = (event) => {
    event.persist();
    setFundedCompany(prevState => ({
      ...prevState,
      logo: event.target.files[0],
      logoPreview: event.target.files[0] ? URL.createObjectURL(event.target.files[0]) : fundedCompany.logoPreview,
    }));
  }

  const handleOnClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  }

  return (
    <Modal
      onClose={handleOnClose}
      open={open}
    >
      <Card
        className={classes.root}
      >
        <CardHeader title='Funded Company' />
        <Divider />
        <CardContent>
          <Grid container spacing={3} >
            <Grid
              container

              xs={3}
            >
              <span className={classes.logoContainer}>
                <img src={fundedCompany?.logoPreview} className={classes.logo} />

                <div className={classes.logoActionsContainer}>
                  <input type='file' name='logo' onChange={handleChangeFileInput} />
                </div>
              </span>
              {
                  fundedCompanyId !== undefined &&
                  <span className={classes.logoContainer}>
                    <RegenerateBio
                        show={adminUserSession.isSuperAdmin || adminUserSession.inHouseResearch}
                        onRegenerate={onRegenerateAiBio}
                    />
                  </span>
              }

            </Grid>

            <Grid
              container

              spacing={2}
              xs={9}
            >
              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    label="Name"
                    variant="outlined"
                    name='name'
                    required
                    inputProps={{ maxLength: 40 }}
                    value={fundedCompany.name || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['name']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    label="Url"
                    variant="outlined"
                    name='url'
                    inputProps={{ maxLength: 150 }}
                    value={fundedCompany.url || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['url']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    label="CRD ID"
                    variant="outlined"
                    name='crd_id'
                    value={fundedCompany.crd_id || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['crd_id']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={12}
              >
                <FormControl fullWidth>
                  <TextField
                    label="Address"
                    variant="outlined"
                    name='address'
                    inputProps={{ maxLength: 40 }}
                    value={fundedCompany.address || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['address']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    label="State"
                    variant="outlined"
                    name='state'
                    inputProps={{ maxLength: 40 }}
                    value={fundedCompany.state || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['state']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    label="City"
                    variant="outlined"
                    name='city'
                    inputProps={{ maxLength: 40 }}
                    value={fundedCompany.city || ''}
                    onChange={handleOnChange}
                  />
                  <span>{errorMessage['city']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    select
                    fullWidth
                    required
                    variant='outlined'
                    value={fundedCompany.region_id || ''}
                    label='Region'
                    name='region_id'
                    onChange={handleOnChangeRegion}
                  >
                    <MenuItem value="">
                      <em>Chose a Region</em>
                    </MenuItem>
                    {
                      regions.map((region, index) => <MenuItem key={`${region.name}-${region.id}`} value={region.id}>{region.name}</MenuItem>)
                    }
                  </TextField>
                  <span>{errorMessage['region_id']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={6}
              >
                <FormControl fullWidth>
                  <TextField
                    select
                    fullWidth
                    required
                    variant='outlined'
                    value={fundedCompany.country_id || ''}
                    label='Country'
                    name='country_id'
                    onChange={handleOnChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: '300px',
                          },
                        },
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Chose a Country</em>
                    </MenuItem>
                    {
                      countries.map((country, index) => <MenuItem key={`${country.name}-${country.id}`} value={country.id}>{country.name}</MenuItem>)
                    }
                  </TextField>
                  <span>{errorMessage['country_id']}</span>
                </FormControl>
              </Grid>

              <Grid
                item

                xs={12}
              >
                <FormControl fullWidth>
                  <TextField
                    select
                    label='Industries'
                    fullWidth
                    variant='outlined'
                    name='industry_ids'
                    className={classes.selectMultiple}
                    required
                    SelectProps={{
                      multiple: true,
                      value: fundedCompany.industry_ids || [],
                      onChange: handleOnChange,
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: '300px',
                          },
                        },
                      },
                      renderValue: (selected) => {
                        return (
                          <div className={classes.chips}>
                            {industries.filter((sector) => selected.includes(sector.id)).map((sector) => (
                              <Chip
                                key={sector.id}
                                label={sector.name}
                                className={classes.chip}
                                clickable
                                deleteIcon={
                                  <Cancel
                                    onMouseDown={(event) => event.stopPropagation()}
                                  />
                                }
                                onDelete={(e) => handleRemoveFromMultiple(e, sector.id, 'industry_ids')}
                              />
                            ))}
                          </div>
                        )
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Chose Industries</em>
                    </MenuItem>
                    {
                      industries.map((sector, index) => <MenuItem key={`${sector.name}-${sector.id}`} value={sector.id}>{sector.name}</MenuItem>)
                    }
                  </TextField>
                  <span>{errorMessage['industry_ids']}</span>
                </FormControl>
              </Grid>

              {fundedCompanyId &&
                <>
                  <Grid item xs={9}>
                    <Autocomplete
                      name="funded_data_merge"
                      open={fundedData.open}
                      onOpen={(event) => handleOpenFundedData(true)}
                      onClose={(event) => handleOpenFundedData(false)}
                      onChange={(event, value) => handleOnChangeAutocomplete(value)}
                      getOptionSelected={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name ? `${option.name} (${option.funded_type || 'Company'})` : ''}
                      value={fundedCompany.item_to_merge || {}}
                      options={fundedData.data}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Funded Companies / Properties"
                          variant="outlined"
                          onChange={(event) => handleInputChangeAutocomplete(event)}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Button color="primary" fullWidth={true} variant="contained" onClick={handleMerge} style={{minHeight: '53px'}} disabled={!fundedCompany.item_to_merge}>Merge it</Button>
                  </Grid>
                </>
              }

            </Grid>

          </Grid>
        </CardContent>
        <Divider />
        <CardActions>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" variant="contained" onClick={handleSubmit}>Save</Button>
        </CardActions>

        {
          showSnackBar ?
            <CustomSnackbar
              open={showSnackBar}
              closeHandler={handleCloseSnackBar}
              message={snackBarMessage}
              options={{
                severity: 'success'
              }}
            />
            : null
        }
      </Card>
    </Modal>
  )
}

export default FundedCompanyForm;